/**
 * TranspositionTable - Position caching for search optimization
 *
 * Uses a hash-based lookup to avoid re-evaluating positions we've seen before.
 * Stores search results including depth, score, and best move found.
 */

import { GameStateSnapshot, GeneratedMove, positionToKey } from '../V3Types';

// Entry types for alpha-beta search
export enum EntryType {
  EXACT = 'exact',         // Exact score (between alpha and beta)
  LOWER_BOUND = 'lower',   // Score >= stored value (beta cutoff)
  UPPER_BOUND = 'upper'    // Score <= stored value (failed high)
}

// Stored entry in the transposition table
export interface TranspositionEntry {
  depth: number;
  score: number;
  type: EntryType;
  bestMove: GeneratedMove | null;
}

// Internal storage entry with hash verification
interface StoredEntry extends TranspositionEntry {
  hash: string;
}

/**
 * Transposition table for caching search results
 */
export class TranspositionTable {
  private table: Map<string, StoredEntry>;
  private maxSize: number;
  private hits: number = 0;
  private misses: number = 0;
  private collisions: number = 0;

  constructor(maxSize: number = 100000) {
    this.table = new Map();
    this.maxSize = maxSize;
  }

  /**
   * Generate a hash key for a game state
   * Uses a simplified string-based hash (Zobrist would be faster but more complex)
   */
  private computeHash(snapshot: GameStateSnapshot): string {
    // Include all relevant state in the hash
    const parts: string[] = [
      `t:${snapshot.turn}`,
      `f:${snapshot.currentFaction}`
    ];

    // Hash units by position and key stats
    const sortedUnits = [...snapshot.units].sort((a, b) => {
      if (a.position.x !== b.position.x) return a.position.x - b.position.x;
      if (a.position.y !== b.position.y) return a.position.y - b.position.y;
      return a.id.localeCompare(b.id);
    });

    for (const unit of sortedUnits) {
      parts.push(`u:${positionToKey(unit.position)}:${unit.faction}:${unit.type}:${unit.health}:${unit.hasAttacked ? 1 : 0}:${unit.movementRemaining}`);
    }

    // Hash cities
    const sortedCities = [...snapshot.cities].sort((a, b) => a.name.localeCompare(b.name));
    for (const city of sortedCities) {
      parts.push(`c:${city.name}:${city.faction ?? 'n'}:${city.level}:${city.isBeingCaptured ? 1 : 0}`);
    }

    // Hash resources
    const ourResources = snapshot.resources.get(snapshot.currentFaction) || 0;
    const enemyResources = snapshot.resources.get(snapshot.enemyFaction) || 0;
    parts.push(`r:${ourResources}:${enemyResources}`);

    return parts.join('|');
  }

  /**
   * Look up a position in the table
   */
  lookup(snapshot: GameStateSnapshot): TranspositionEntry | null {
    const hash = this.computeHash(snapshot);
    const entry = this.table.get(hash);

    if (!entry) {
      this.misses++;
      return null;
    }

    // Verify hash matches (collision detection)
    if (entry.hash !== hash) {
      this.collisions++;
      return null;
    }

    this.hits++;
    return {
      depth: entry.depth,
      score: entry.score,
      type: entry.type,
      bestMove: entry.bestMove
    };
  }

  /**
   * Store a position in the table
   */
  store(snapshot: GameStateSnapshot, entry: TranspositionEntry): void {
    const hash = this.computeHash(snapshot);

    // Check if we should replace existing entry
    const existing = this.table.get(hash);
    if (existing) {
      // Replace if new entry is deeper or same depth with better info
      if (entry.depth < existing.depth) {
        return;  // Keep deeper search result
      }
    }

    // Evict if table is full (simple strategy: just clear oldest half)
    if (this.table.size >= this.maxSize) {
      this.evict();
    }

    this.table.set(hash, {
      ...entry,
      hash
    });
  }

  /**
   * Evict entries when table is full
   * Simple strategy: clear half the table
   */
  private evict(): void {
    const entries = Array.from(this.table.entries());
    const toKeep = entries.slice(entries.length / 2);
    this.table.clear();
    for (const [key, value] of toKeep) {
      this.table.set(key, value);
    }
  }

  /**
   * Clear the entire table
   */
  clear(): void {
    this.table.clear();
    this.hits = 0;
    this.misses = 0;
    this.collisions = 0;
  }

  /**
   * Get table statistics
   */
  getStats(): { size: number; hits: number; misses: number; collisions: number; hitRate: number } {
    const total = this.hits + this.misses;
    return {
      size: this.table.size,
      hits: this.hits,
      misses: this.misses,
      collisions: this.collisions,
      hitRate: total > 0 ? this.hits / total : 0
    };
  }

  /**
   * Prefetch best move from previous search (for move ordering)
   */
  getBestMove(snapshot: GameStateSnapshot): GeneratedMove | null {
    const entry = this.lookup(snapshot);
    return entry?.bestMove ?? null;
  }
}

/**
 * Zobrist hashing utilities (for potential future optimization)
 *
 * Zobrist hashing uses XOR of random bitstrings for each piece/position combination.
 * This allows incremental hash updates when making/unmaking moves.
 *
 * For now, we use string hashing which is simpler but slower.
 * Zobrist can be implemented later if performance requires it.
 */

// Reserved for future Zobrist implementation
export interface ZobristTable {
  // Random bitstrings for each (unit type, faction, position) combination
  unitHashes: Map<string, bigint>;
  // Random bitstrings for city ownership
  cityHashes: Map<string, bigint>;
  // Random bitstrings for turn/faction to move
  turnHashes: Map<string, bigint>;
}

// Placeholder for Zobrist table initialization
export function initZobristTable(): ZobristTable {
  return {
    unitHashes: new Map(),
    cityHashes: new Map(),
    turnHashes: new Map()
  };
}
